/**
 * What a late return is costing, while it is still running.
 *
 * MIRROR of app/constants/lateFee.ts. The rounding is the thing that has to
 * match: both charge per STARTED hour (Math.ceil), the same rule
 * requestBikeReturn uses when it finally writes the fee onto the booking. A
 * running figure that disagreed with the final charge would be worse than no
 * figure at all.
 *
 * NOTHING WRITES THIS DOWN WHILE THE RENTAL RUNS. `booking.lateFee` only
 * appears when the renter starts the return. Until then the fee exists only
 * as arithmetic over the booked end time and the rate, which is why the
 * panel showed nothing for a rental that was eleven hours late and climbing.
 *
 * TIME ZONE. Bookings store their end as display text in Maldives time
 * ("Tue, September 22, 2026" / "9:00 AM"). The app parses that in the
 * device's local zone, which for a phone in the Maldives is the same thing.
 * A browser is not necessarily anywhere, so this parses +05:00 explicitly —
 * the same expression functions/returnReminders.js and functions/stuckSteps.js
 * use, so the panel agrees with the server wherever it is opened from.
 */
import { useEffect, useState } from 'react';

const HOUR_MS = 60 * 60 * 1000;

/** The booked return as an instant, or null when the booking has no usable end. */
export function bookingEndMs(booking) {
  if (!booking?.endDate || !booking?.endTime) return null;
  const t = new Date(`${booking.endDate} ${booking.endTime} GMT+0500`).getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * The rate this booking would actually charge.
 *
 * Frozen onto the booking at booking time (functions/priceCheck.js), so an
 * owner raising their listing's rate mid-rental does not change it. Absent or
 * zero means no late fee is ever charged for this rental — not that the rate
 * is unknown.
 */
export const lateRate = (booking) => Number(booking?.lateFeePerHour) || 0;

/**
 * What has accrued so far, or null when nothing is accruing.
 *
 * Null covers four different "no": the rental is not running, it is not past
 * its return time, the booking has no usable end time, or the owner set no
 * late fee. None of them is an error and none of them should show a figure.
 */
export function lateFeeSoFar(booking, nowMs = Date.now()) {
  if (booking?.status !== 'ongoing') return null;
  const rate = lateRate(booking);
  if (rate <= 0) return null;
  const end = bookingEndMs(booking);
  if (end == null) return null;

  const msLate = nowMs - end;
  if (msLate <= 0) return null;

  const hours = Math.ceil(msLate / HOUR_MS);
  return {
    hours,
    ratePerHour: rate,
    amount: hours * rate,
    msLate,
    // How long until the next whole hour is charged. The reason this panel
    // ticks: the number on screen is about to change.
    minutesToNextHour: Math.max(0, Math.ceil((HOUR_MS - (msLate % HOUR_MS)) / 60000)),
  };
}

/** "2d 4h late", "3h 15m late", "20m late". */
export function overdueLabel(msLate) {
  if (!(msLate > 0)) return '';
  const totalMinutes = Math.floor(msLate / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h late`;
  if (hours > 0) return `${hours}h ${minutes}m late`;
  return `${minutes}m late`;
}

/** MVR 1,250 — grouped, because these reach four figures. */
export const mvr = (n) => `MVR ${Number(n || 0).toLocaleString('en-US')}`;

/**
 * A clock that moves.
 *
 * Every minute, not every second: the charge changes on the hour and the only
 * sub-hour figure on screen is a minutes countdown. A per-second re-render of
 * the whole bookings list to move a number that does not change would be
 * sixty times the work for none of the information.
 */
export function useNow(intervalMs = 60000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}
